<template>
    <DCard
        :title="__('settings.orders.order_clients.OSA')"
        padding
    >
        <DFormRow
            :label="__('settings.order_client.form.order_client')"
            :hint="__('settings.order_client.form.order_client_hint_osa')"
        >
            <DSwitch
                v-model="form.enabled"
                color="success"
                variant="3d"
                @update:modelValue="$emit('osa-valid', !form.enabled || credentialsAreValid)"
            />
        </DFormRow>
        <DRow v-if="form.enabled">
            <DFormRow
                :label="__('settings.order_client.form.api_url')"
                :is-required="true"
                :error="errorsData.api_url ?? errorsData['OSA.api_url']"
                :hint="__('settings.order_client.form.api_url_hint_osa')"
            >
                <DInput
                    id="api_url"
                    v-model="form.api_url"
                    :placeholder="__('settings.order_client.form.api_url')"
                    :is-valid="isFormFieldValid('api_url')"
                    type="text"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.api_key')"
                :is-required="true"
                :error="errorsData.api_key ?? errorsData['OSA.api_key']"
                :hint="__('settings.order_client.form.api_key_hint_osa')"
            >
                <DInput
                    id="api_key"
                    v-model="form.api_key"
                    :placeholder="__('settings.order_client.form.api_key')"
                    :is-valid="isFormFieldValid('api_key')"
                    type="text"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.transfer_url')"
                :is-required="true"
                :error="errorsData.transfer_url ?? errorsData['OSA.transfer_url']"
                :hint="__('settings.order_client.form.transfer_url_hint_osa')"
            >
                <DInput
                    id="transfer_url"
                    v-model="form.transfer_url"
                    :placeholder="__('settings.order_client.form.transfer_url')"
                    :is-valid="isFormFieldValid('transfer_url')"
                    type="text"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.osa_brand_ids')"
                :is-required="true"
                :error="errorsData.osa_brand_ids ?? errorsData['OSA.osa_brand_ids']"
                :hint="__('settings.order_client.form.osa_brand_ids_hint')"
            >
                <DInput
                    id="osa_brand_ids"
                    v-model="form.osa_brand_ids"
                    :placeholder="__('settings.order_client.form.osa_brand_ids')"
                    :is-valid="isFormFieldValid('osa_brand_ids')"
                    type="text"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.osa_brand_mapping')"
                :is-required="false"
                :error="errorsData.osa_brand_ids ?? errorsData['OSA.osa_brand_mapping']"
                :hint="__('settings.order_client.form.osa_brand_mapping_hint')"
            >
                <DInput
                    id="osa_brand_mapping"
                    v-model="form.osa_brand_mapping"
                    :placeholder="__('settings.order_client.form.osa_brand_mapping')"
                    :is-valid="isFormFieldValid('osa_brand_mapping')"
                    type="text"
                />
            </DFormRow>
            <DRow>
                <DCol sm="4" />
                <DCol sm="4">
                    <div
                        v-if="connectionStatus === 'successful'"
                        class="green-text"
                    >
                        {{ __('settings.connections.connection_successful') }}
                    </div>
                    <div
                        v-if="connectionStatus === 'unsuccessful'"
                        class="red-text"
                    >
                        {{ __('settings.connections.connection_unsuccessful') }}
                    </div>
                </DCol>
                <DCol
                    sm="4"
                    class="check-database-connection-btn"
                >
                    <br>
                    <DButton
                        icon="lan-connect"
                        :label="__('settings.connections.check_connection')"
                        :disabled="isInLoadingState"
                        @click="checkConnection"
                    />
                </DCol>
                <DConnectionErrorModal
                    :show-modal="connectionException !== null"
                    :exception="connectionException"
                    @close-modal="closeErrorCheckModal"
                />
            </DRow>
        </DRow>
    </DCard>
</template>

<script setup>
import { route } from '~@/Composables/route'
import { useForm } from '@inertiajs/vue3'
import { onUpdated, ref } from 'vue'

import DConnectionErrorModal from '~@/Components/Common/DConnectionErrorModal'

const props = defineProps({
    osaSettings: {
        type: Object,
        default: () => { return {} }
    },
    errors: {
        type: Object,
        default: () => { return {} }
    }
})
const emit = defineEmits(['osa-valid', 'osa-data'])

const form = useForm({
    api_url: props.osaSettings.api_url ?? null,
    api_key: props.osaSettings.api_key,
    transfer_url: props.osaSettings.transfer_url ?? null,
    osa_brand_ids: props.osaSettings.osa_brand_ids ?? null,
    osa_brand_mapping: props.osaSettings.osa_brand_mapping ?? null,
    order_client: props.osaSettings.order_client ?? 'OSA',
    enabled: props.osaSettings.enabled ?? false
})

const connectionStatus = ref('')
const apiError = ref(null)
const errorsData = ref(props.errors)
const credentialsAreValid = ref(false)
const connectionException = ref(null)

emit('osa-data', form)
emit('osa-valid', !form.enabled || credentialsAreValid.value)

const checkConnection = () => {
    errorsData.value = {}
    connectionException.value = null
    window.axios.post(route('settings.order-settings.check-osa', form))
        .then(function () {
            connectionStatus.value = 'successful'
            credentialsAreValid.value = true
            emit('osa-valid', !form.enabled || credentialsAreValid.value)
        })
        .catch(function (error) {
            connectionStatus.value = 'unsuccessful'
            credentialsAreValid.value = false
            if (error.response.data.api_error !== undefined) {
                apiError.value = error.response.data.api_error
            }

            if (error.response.data.errors !== undefined) {
                errorsData.value = error.response.data.errors
                for (const key in errorsData.value) {
                    if (typeof errorsData.value[key] === 'object') {
                        errorsData.value[key] = errorsData.value[key][0]
                    }
                }
            }
            if (error?.response.data.exception !== undefined) {
                connectionException.value = error.response.data.exception
            }
        })
}

const closeErrorCheckModal = () => {
    apiError.value = null
    connectionException.value = null
}

onUpdated(() => {
    errorsData.value = props.errors
})

</script>
<script>
export default {
    name: 'OsaCreate'
}
</script>

<style lang="scss" scoped>
.check-database-connection-btn {
    text-align: right;
    padding-bottom: 12px;
}
.green-text {
    color: green;
}
.red-text {
    color: red;
}
</style>
